'use strict';

const csrf = require('public/js/lib/csrf/utils');

/**
 * Sets the CSRF token into the meta tag, creating it if it doesn't exist.
 * @param {string} token - The CSRF token to set in the meta tag.
 * @returns {void}
 */
function setMetaToken(token) {
	try {
		const csrfConfig = csrf.getCsrfConfig();
		const meta = document.querySelector(`meta[name="${csrfConfig.meta_name}"]`);
		if (meta && typeof meta.setAttribute === 'function') {
			meta.setAttribute('content', token);
			return;
		}

		const newMeta = document.createElement('meta');
		newMeta.setAttribute('name', csrfConfig.meta_name);
		newMeta.setAttribute('content', token);
		document.head.appendChild(newMeta);
	} catch (e) {
		console.warn('[csrf-bootstrap] setMetaToken failed', e); // eslint-disable-line no-console
	}
}

/**
 * Sets the CSRF token into the hidden input field, creating it if it doesn't exist.
 * @param {string} token - The CSRF token to set in the hidden input field.
 * @returns {void}
 */
function setHiddenInputToken(token) {
	try {
		const csrfConfig = csrf.getCsrfConfig();
		let valueField = document.getElementById(csrfConfig.input_id);
		if (!valueField) {
			valueField = document.createElement('input');
			valueField.setAttribute('type', 'hidden');
			valueField.setAttribute('id', csrfConfig.input_id);
			if (csrfConfig.field_name) {
				valueField.setAttribute('name', csrfConfig.field_name);
			}
			document.body.appendChild(valueField);
		}

		if (typeof valueField.setAttribute === 'function') {
			valueField.setAttribute('value', token);
		}
	} catch (e) {
		console.warn('[csrf-bootstrap] setHiddenInputToken failed', e); // eslint-disable-line no-console
	}
}

/**
 * Retrieves the CSRF input template from the DOM.
 * @returns {HTMLInputElement|null}
 */
function getCsrfInputTemplate() {
	const csrfConfig = csrf.getCsrfConfig();
	const el = document.getElementById(csrfConfig.input_id);
	if (el?.tagName !== 'INPUT') {
		return null;
	}
	return /** @type {HTMLInputElement} */ (el);
}

/**
 * Injects the CSRF token into the form if it is a valid form and the CSRF token input is not already present.
 * @param {HTMLFormElement} form - The form to inject the CSRF token into.
 * @returns {void}
 */
function injectTokenIntoForm(form) {
	const template = getCsrfInputTemplate();
	const isValidForm = form instanceof HTMLFormElement;

	if (!template || !isValidForm) {
		return;
	}

	const method = String(form.getAttribute('method') || 'GET');
	const action = String(form.getAttribute('action') || '');
	const csrfConfig = csrf.getCsrfConfig();
	const templateToken = String(template.getAttribute('value') || '');
	const isMutatingSameOrigin = csrf.isMutatingMethod(method) && csrf.isSameOriginFormAction(action);

	if (!isMutatingSameOrigin) {
		return;
	}

	const csrfInputExists = form.querySelector(`input[name="${csrfConfig.field_name}"]`);
	if (csrfInputExists) {
		const existingToken = String(csrfInputExists.getAttribute('value') || '');
		if (templateToken && existingToken !== templateToken && typeof csrfInputExists.setAttribute === 'function') {
			csrfInputExists.setAttribute('value', templateToken);
		}
		return;
	}

	const input = /** @type {HTMLInputElement} */ (template.cloneNode(true));
	input.removeAttribute('id');
	delete input.dataset.csrfTemplate;
	form.appendChild(input);
}

/**
 * Injects the CSRF token into all forms present on the page.
 * @returns {void}
 */
function injectTokenIntoForms() {
	try {
		for (const form of Array.from(document.querySelectorAll('form'))) {
			injectTokenIntoForm(form);
		}
	} catch (e) {
		console.warn('[csrf-bootstrap] injectTokenIntoForms failed', e); // eslint-disable-line no-console
	}
}

/**
 * Injects the CSRF token into forms added to the DOM dynamically.
 * @param {Node} node - The node to inject the CSRF token into.
 * @returns {void}
 */
function injectTokenIntoFormsFromNode(node) {
	const isValidNode = node && node instanceof Element;
	if (!isValidNode) {
		return;
	}

	const isForm = node.tagName.toLowerCase() === 'form';

	if (isForm) {
		return injectTokenIntoForm(node);
	}

	for (const nestedForm of Array.from(node.querySelectorAll('form'))) {
		injectTokenIntoForm(nestedForm);
	}
}

/**
 * Starts the form injection observer to handle injection of CSRF tokens into forms added to the DOM dynamically.
 * @returns {void}
 */
function startFormInjectionObserver() {
	try {
		if (globalThis.__rp_csrf_form_observer) {
			return;
		}

		const observer = new MutationObserver((mutations) => {
			try {
				for (const mutation of mutations) {
					const addedNodes = Array.from(mutation?.addedNodes ?? []);
					if (addedNodes.length === 0) {
						continue;
					}

					for (const node of addedNodes) {
						injectTokenIntoFormsFromNode(node);
					}
				}
			} catch (e) {
				console.warn('[csrf-bootstrap] MutationObserver callback failed', e); // eslint-disable-line no-console
			}
		});

		observer.observe(document.body, { childList: true, subtree: true });
		globalThis.__rp_csrf_form_observer = observer;
	} catch (e) {
		console.warn('[csrf-bootstrap] startFormInjectionObserver failed', e); // eslint-disable-line no-console
	}
}

module.exports = {
	injectTokenIntoForm,
	injectTokenIntoForms,
	setHiddenInputToken,
	setMetaToken,
	startFormInjectionObserver,
};
